import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useSignIn } from "@/api/sign-in";
import { Button, Form, FormGroup, Input, Stack, Text } from "@/components/ui";
import { useModalContext } from "@/contexts/modal-context";

const messages = defineMessages({
  label: {
    id: "landing-page.sign-in.label",
    defaultMessage: "Sign in with your username and password",
  },
  username: {
    id: "modals.sign-in.labels.username",
    defaultMessage: "Username",
  },
  password: {
    id: "modals.sign-in.labels.password",
    defaultMessage: "Password",
  },
  signIn: { id: "landing-page.sign-in", defaultMessage: "Sign In" },
  forgot: {
    id: "landing-page.sign-in.forgot",
    defaultMessage: "Forgot password?",
  },
});

/** Inline sign-in form on the landing page. Real auth via {@link useSignIn};
 *  the MFA / new-device steps are handed off to the SIGNIN modal. */
const SignInForm: React.FC = () => {
  const intl = useIntl();
  const { openModal } = useModalContext();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const { submit, errors, isLoading } = useSignIn({
    onMfaRequired(mfaToken) {
      openModal("SIGNIN", { initialMfaToken: mfaToken });
    },
    onNewDevice(challenge) {
      openModal("SIGNIN", { initialNewDevice: challenge });
    },
  });

  const handleSubmit: React.FormEventHandler = (event) => {
    event.preventDefault();
    submit(username, password);
  };

  return (
    <Stack space={4}>
      <Text size="md" weight="medium">
        {intl.formatMessage(messages.label)}
      </Text>

      <Form onSubmit={handleSubmit} className="space-y-6">
        <Stack space={3}>
          <FormGroup errors={errors.username ? [errors.username] : []}>
            <Input
              aria-label={intl.formatMessage(messages.username)}
              placeholder={intl.formatMessage(messages.username)}
              type="text"
              name="username"
              autoCorrect="off"
              autoCapitalize="off"
              required
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className="rounded-lg border"
              data-testid="username"
            />
          </FormGroup>

          <FormGroup errors={errors.password ? [errors.password] : []}>
            <Input
              aria-label={intl.formatMessage(messages.password)}
              placeholder={intl.formatMessage(messages.password)}
              type="password"
              name="password"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="rounded-lg border"
              data-testid="password"
            />
          </FormGroup>
        </Stack>

        <Stack space={3}>
          <Button type="submit" block disabled={isLoading} data-testid="submit">
            {intl.formatMessage(messages.signIn)}
          </Button>

          <Button
            block
            variant="link"
            type="button"
            onClick={() => openModal("RESET_USERNAME_PASSWORD")}
          >
            {intl.formatMessage(messages.forgot)}
          </Button>
        </Stack>
      </Form>
    </Stack>
  );
};

export default SignInForm;
